import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { Budget, CategoryItem, ResponsibleItem, DestinationItem } from '../types/budget';
import { useBudgetEntities } from '../hooks/useBudgetEntities';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface EntitiesManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  budget: Budget;
}

export const EntitiesManagementDialog: React.FC<EntitiesManagementDialogProps> = ({
  isOpen,
  onClose,
  budget,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'categories' | 'responsibles' | 'destinations'>(
    'categories'
  );
  const [newItemName, setNewItemName] = useState('');
  const [editingItem, setEditingItem] = useState<{ id: string; name: string } | null>(null);

  const {
    addCategory,
    addResponsible,
    addDestination,
    updateCategory,
    updateResponsible,
    updateDestination,
    deleteCategory,
    deleteResponsible,
    deleteDestination,
  } = useBudgetEntities(budget.id || null);

  const handleAddItem = () => {
    if (!newItemName.trim()) return;

    if (activeTab === 'categories') {
      addCategory(newItemName);
    } else if (activeTab === 'responsibles') {
      addResponsible(newItemName);
    } else {
      addDestination(newItemName);
    }

    setNewItemName('');
  };

  const handleUpdateItem = (id: string, newName: string) => {
    if (!newName.trim()) return;

    if (activeTab === 'categories') {
      updateCategory(id, newName);
    } else if (activeTab === 'responsibles') {
      updateResponsible(id, newName);
    } else {
      updateDestination(id, newName);
    }

    setEditingItem(null);
  };

  const handleDeleteItem = (id: string) => {
    if (activeTab === 'categories') {
      deleteCategory(id);
    } else if (activeTab === 'responsibles') {
      deleteResponsible(id);
    } else {
      deleteDestination(id);
    }
  };

  const isItemProtected = (item: CategoryItem | ResponsibleItem | DestinationItem): boolean => {
    if (activeTab === 'responsibles') {
      return !!(item as ResponsibleItem).isProtected;
    } else if (activeTab === 'destinations') {
      return !!(item as DestinationItem).isProtected;
    }
    return false;
  };

  const renderItems = () => {
    let items;
    if (activeTab === 'categories') {
      items = budget.categories;
    } else if (activeTab === 'responsibles') {
      items = budget.responsibles;
    } else {
      items = budget.destinations;
    }

    if (items.length === 0) {
      return <div className="text-slate-500 dark:text-slate-400">{t('entities.noItems')}</div>;
    }

    return items.map((item) => (
      <div
        key={item.id}
        className="mb-2 flex items-center justify-between rounded-md border border-slate-200 p-3 dark:border-surface-700"
      >
        {editingItem && editingItem.id === item.id ? (
          <div className="flex w-full items-center gap-2">
            <input
              type="text"
              value={editingItem.name}
              onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); handleUpdateItem(item.id, editingItem.name); }
                if (e.key === 'Escape') { setEditingItem(null); }
              }}
              className="flex-1 rounded-md border border-slate-300 p-2 dark:border-surface-600 dark:bg-surface-700 dark:text-white"
              placeholder={
                activeTab === 'categories'
                  ? t('entities.new.category')
                  : activeTab === 'responsibles'
                    ? t('entities.new.responsible')
                    : t('entities.new.destination')
              }
            />
            <button
              onClick={() => handleUpdateItem(item.id, editingItem.name)}
              className="rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400"
            >
              {t('common.save')}
            </button>
            <button
              onClick={() => setEditingItem(null)}
              className="rounded-md bg-slate-300 px-3 py-2 text-slate-700 hover:bg-slate-400 dark:bg-surface-600 dark:text-slate-200 dark:hover:bg-surface-500"
            >
              {t('common.cancel')}
            </button>
          </div>
        ) : (
          <>
            <span className="flex-1 text-slate-700 dark:text-slate-200">{item.name}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setEditingItem({ id: item.id, name: item.name })}
                disabled={isItemProtected(item)}
                className={`rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-surface-700 dark:hover:text-slate-200 ${
                  isItemProtected(item) ? 'cursor-not-allowed opacity-50' : ''
                }`}
                title={isItemProtected(item) ? t('entities.protected') : t('common.edit')}
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleDeleteItem(item.id)}
                disabled={isItemProtected(item)}
                className={`rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-surface-700 dark:hover:text-slate-200 ${
                  isItemProtected(item) ? 'cursor-not-allowed opacity-50' : ''
                }`}
                title={isItemProtected(item) ? t('entities.protected') : t('common.delete')}
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </>
        )}
      </div>
    ));
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center">
        <div
          className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50"
          aria-hidden="true"
        />

        <div className="relative mx-4 w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-surface-800">
          <Dialog.Title className="mb-4 text-lg font-medium text-slate-900 dark:text-white">
            {t('entities.management')}
          </Dialog.Title>

          <div className="mb-6">
            <div className="mb-4 border-b border-slate-200 dark:border-surface-700">
              <ul className="-mb-px flex">
                <li className="mr-2">
                  <button
                    onClick={() => setActiveTab('categories')}
                    className={`inline-block rounded-t-lg border-b-2 p-4 ${
                      activeTab === 'categories'
                        ? 'border-slate-900 text-slate-900 dark:border-indigo-400 dark:text-white'
                        : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300'
                    }`}
                  >
                    {t('entities.categories')}
                  </button>
                </li>
                <li className="mr-2">
                  <button
                    onClick={() => setActiveTab('responsibles')}
                    className={`inline-block rounded-t-lg border-b-2 p-4 ${
                      activeTab === 'responsibles'
                        ? 'border-slate-900 text-slate-900 dark:border-indigo-400 dark:text-white'
                        : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300'
                    }`}
                  >
                    {t('entities.responsibles')}
                  </button>
                </li>
                <li className="mr-2">
                  <button
                    onClick={() => setActiveTab('destinations')}
                    className={`inline-block rounded-t-lg border-b-2 p-4 ${
                      activeTab === 'destinations'
                        ? 'border-slate-900 text-slate-900 dark:border-indigo-400 dark:text-white'
                        : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300'
                    }`}
                  >
                    {t('entities.destinations')}
                  </button>
                </li>
              </ul>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleAddItem(); }} className="mb-4 flex items-center gap-2">
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                className="flex-1 rounded-md border border-slate-300 p-2 dark:border-surface-600 dark:bg-surface-700 dark:text-white"
                placeholder={
                  activeTab === 'categories'
                    ? t('entities.new.category')
                    : activeTab === 'responsibles'
                      ? t('entities.new.responsible')
                      : t('entities.new.destination')
                }
              />
              <button
                type="submit"
                className="flex items-center gap-1 rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400"
              >
                <PlusIcon className="h-5 w-5" />
                <span>{t('common.add')}</span>
              </button>
            </form>

            <div className="h-[300px] overflow-y-auto">{renderItems()}</div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-500 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-slate-500 dark:bg-surface-700 dark:text-slate-300 dark:hover:bg-surface-600"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
